import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, { getName } from "../../../framework/src/Messages/MessageEnum";

// Customizable Area Start
import { makeApiMessage as MakeApiMessage } from '../../../components/src/common'
import { IUserContext } from "../../../blocks/navigationmenu/src/PageContainerController.web";
import {
  PermissionStatus,
  checkForNewPermissonStatus,
  customPermissionApiKey
} from "../../../blocks/utilities/src/CustomBlockHelpers";
import { PermissionGroupArray } from "../../../blocks/navigationmenu/src/utils";

export interface ITimeSlot {
  id: number;
  end_time: Date;
  start_time: Date;
  promotion_id: number;
  endDate: Date | string | null | undefined | never;
  startDate: Date | string | null | undefined | never;
}

interface IOption {
  id: number;
  name: string;
  area_name?: string;
  store_name?: string;
  branch_name?: string;
  region_name?: string;
  product_name?: string;
  product_type?: string;
}

export interface IPromotionItem {
  id: number;
  item_type: string;
  catalogue_id: number | null;
  product_name?: string;
  applicable_products_count?: number;
  catalogue?: {
    id: number;
    product_name: string;
  } | null;
}

export interface IPromotionOfferItem {
  id: number;
  offer_type: string;
  item_type: string | null;
  catalogue_id: number | null;
  offer_description?: string;
  catalogue?: {
    id: number;
    product_name: string;
  } | null;
}

export interface IData {
  id: string;
  attributes: {
    promotion_name: string;
    promotion_type: {
      id: number;
      name: string;
      buy_quantity?: number;
      get_quantity?: number;
      display_name?: string;
    };
    promo_item_text_en: string;
    promo_item_text_ar: string;
    order_discount_name_en: string;
    order_discount_name_ar: string;
    used: string;
    status: string;
    no_of_orders: number | string | null;
    minimum_order_amount: number | string;
    minimum_items: number | string;
    start_date: string | Date | null | never;
    end_date: string | Date | null | never;
    sections: Array<string | never>;
    services: Array<string | never>;
    products: Array<string | never>;
    platforms: Array<string | never>;
    regions: Array<string | never>;
    areas: Array<string | never>;
    stores: Array<string | never>;
    excluded_days: Array<string | never>;
    promotion_items: Array<IPromotionItem>;
    promotion_offer_items: Array<IPromotionOfferItem>;
    excluded_dates: Array<{id: number, excluded_date: string, reason?: string, display_date?: string}>;
    excluded_times: Array<{id: number, start_time: string, end_time: string, display_range?: string}>;
    is_currently_active?: boolean;
    is_expired?: boolean;
    is_upcoming?: boolean;
    all_sections_enabled?: boolean;
    all_products_enabled?: boolean;
  };
}

interface Props {
  navigation: {
    navigate: Function;
    getParam: Function;
  };
}

interface State {
  noData: boolean;
  isLoading: boolean;
  isViewLoading: boolean;
  data: IData | never;
  permissionStatus: PermissionStatus;
  isLoadingPermission: boolean;
}

interface SS {}
// Customizable Area End

// Customizable Area Start
const configJSON = require("./config");
const ApiUrls = configJSON.ApiUrls;
// Customizable Area End

class PromotionViewController extends BlockComponent<Props, State, SS> {
  constructor(props: Props) {
    super(props);

    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.LayoutDataMessage),
    ];
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
    // Customizable Area End
  }

  // Customizable Area Start
  promotionShowMessageId: string = "";
  // Customizable Area End

  state: State = {
    // Customizable Area Start
    noData: false,
    isLoading: true,
    isViewLoading: false,
    data: {
      id: "",
      attributes: {
        promotion_name: "",
        promotion_type: {
          id: 0,
          name: "",
          buy_quantity: 0,
          get_quantity: 0,
          display_name: ""
        },
        promo_item_text_en: "",
        promo_item_text_ar: "",
        order_discount_name_en: "",
        order_discount_name_ar: "",
        used: "",
        status: "",
        no_of_orders: null,
        minimum_order_amount: "",
        minimum_items: "",
        start_date: "",
        end_date: "",
        sections: [],
        services: [],
        products: [],
        platforms: [],
        regions: [],
        areas: [],
        stores: [],
        excluded_days: [],
        promotion_items: [],
        promotion_offer_items: [],
        excluded_dates: [],
        excluded_times: [],
        is_currently_active: false,
        is_expired: false,
        is_upcoming: false,
        all_sections_enabled: false,
        all_products_enabled: false
      },
    },
    permissionStatus: {
      mainPermission: false,
      createPermission: false,
      viewPermission: false,
      editPermission: false,
      deactivatePermission: false
    },
    isLoadingPermission: true
    // Customizable Area End
  };

  receive(_from: string, message: Message): void {
    // Customizable Area Start
    this.receiveDataFromLayout(message);
    let requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    let successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );
    if (successResponse?.data) {
      if (requestId === this.promotionShowMessageId) {
        let data = successResponse.data;

        // Map sections to display names
        data.attributes.sections = data.attributes.sections?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map services to display names
        data.attributes.services = data.attributes.services?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map products to display names
        data.attributes.products = data.attributes.products?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map platforms to display names
        data.attributes.platforms = data.attributes.platforms?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map stores to display names
        data.attributes.stores = data.attributes.stores?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map areas to display names
        data.attributes.areas = data.attributes.areas?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map regions to display names
        data.attributes.regions = data.attributes.regions?.map(
          (item: IOption) => item.name
        ) || [];
        
        // Map excluded days to display names
        data.attributes.excluded_days = data.attributes.excluded_days?.map(
          (item: {day_of_week: string, display_name?: string}) => item.display_name || item.day_of_week
        ) || [];

        this.setState({isViewLoading: false, isLoading: false, noData: false, data });
      }
    } else if (successResponse?.error) {
      if (requestId === this.promotionShowMessageId) {
        this.setState({isViewLoading: false, isLoading: false, noData: true });
      }
    }

    // Customizable Area End
  }

  async componentDidMount() {
    super.componentDidMount();
    // Customizable Area Start
    this.getPromotionItemApi();
    // Customizable Area End
  }

  // Customizable Area Start

  receiveDataFromLayout = (message: Message) => {
    if (message.id === getName(MessageEnum.LayoutDataMessage)) {
        const recievedData = message.getData(
            getName(MessageEnum.LayoutMessageData)
        );
        if (recievedData.userContext) {
            this.handleUserChange(recievedData.userContext)
        }
    }
  }

  getPromotionItemApi() {
    this.setState({isViewLoading: true})
    const id = this.props.navigation.getParam("id");
    const requestMessage = MakeApiMessage({
      url: ApiUrls.promotionShow + id,
      method: "GET",
    });

    this.promotionShowMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  }

  redirectTo(page: string, props = {}) {
    this.props.navigation.navigate(page, props);
  }

  handleUserChange = (userContext: IUserContext) => {
    const apiKey = customPermissionApiKey.promocodePermissions;
    const userData = userContext.user?.attributes.permission_groups;
    const value = checkForNewPermissonStatus(apiKey, userData as Array<PermissionGroupArray>);
    this.setState({
      permissionStatus: value,
      isLoadingPermission: false
    })
  };

  // Customizable Area End
}

// Customizable Area Start

// Customizable Area End

export default PromotionViewController;